import { arrow, computePosition, flip, offset } from "@floating-ui/dom"

// Debounce utility helper
function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timerId: ReturnType<typeof setTimeout>

  return function debouncedFn(...args: Parameters<T>) {
    clearTimeout(timerId)

    timerId = setTimeout(() => {
      func(...args)
    }, delay)
  }
}

export default function textSnippetShare(el: HTMLElement) {
  function handleTextSelect() {
    const selection = document.getSelection()
    const tooltip = el.querySelector<HTMLDivElement>(".snippet-share-tooltip")

    // If there is no selection or no tooltip in the DOM then do nothing
    if (!selection || !tooltip) {
      return
    }

    // Get twitter link
    const twitterLink = tooltip.querySelector<HTMLAnchorElement>(
      ".snippet-share-twitter",
    )
    if (!twitterLink) {
      return
    }

    // Determine if text selected is within the article body
    if (selection.rangeCount === 0) {
      return
    }
    const range = selection.getRangeAt(0)
    const isTextInEl =
      el.contains(range.startContainer) && el.contains(range.endContainer)

    // If not in article body or there is no range selected then hide the tooltip and do not proceed
    if (!isTextInEl || selection.isCollapsed) {
      // Hide and reset links in the tooltip
      tooltip.classList.add("invisible")
      twitterLink.href = "#"

      return
    }

    // Use floating-ui on how toolip is position when at the top and bottom edges of the viewport
    const arrowEl = tooltip.querySelector<HTMLDivElement>(
      ".snippet-share-arrow",
    )

    if (!arrowEl) {
      return
    }

    computePosition(range, tooltip, {
      placement: "top",
      middleware: [
        offset(8),
        flip({
          fallbackPlacements: ["bottom"],
          padding: {
            top: 66,
          },
        }),
        arrow({ element: arrowEl }),
      ],
    }).then(({ x, y, placement, middlewareData }) => {
      Object.assign(tooltip.style, {
        left: `${x}px`,
        top: `${y}px`,
      })

      if (middlewareData.arrow) {
        const { x: arrowX, y: arrowY } = middlewareData.arrow

        const staticSide = {
          top: "bottom",
          right: "left",
          bottom: "top",
          left: "right",
        }[placement.split("-")[0]] as string

        Object.assign(arrowEl.style, {
          left: arrowX != null ? `${arrowX}px` : "",
          top: arrowY != null ? `${arrowY}px` : "",
          right: "",
          bottom: "",
          [staticSide]: "-4px",
        })
      }
    })

    // Show tooltip
    tooltip.classList.remove("invisible")

    // Configure share link with text content
    const text = encodeURI(selection.toString())
    const url = encodeURI(window.location.href)
    const twitterUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}&via=CultureAmp&related=CultureAmp`
    twitterLink.href = twitterUrl
  }

  document.addEventListener("selectionchange", debounce(handleTextSelect, 250))
}
